'use strict';

/**
 * Single source of truth for city SEO metrics (counts, prices).
 *
 * WHY: seoCount/priceRange were duplicated across three files — seoUtils.js,
 * ColivingSearchSEO.vue and ColivingSearchSchema.js.  When numbers changed
 * they had to be updated in three places (they often weren't).
 *
 * Consumed by:
 *   - frontend/src/utils/seoUtils.js          (CITY_SEO_DATA / getCitySeoCount)
 *   - frontend/src/components/ColivingSearchSEO.vue  (seoCount, priceRange)
 *   - frontend/src/components/schemas/ColivingSearchSchema.js  (cityPrices)
 *   - backend/scripts/generate-sitemap.js      (optional, for future smart-lastmod)
 *
 * Update these numbers quarterly when inventory grows significantly.
 * London neighborhoods are NOT here — they live only in ColivingSearchSEO.vue
 * because they have no duplication elsewhere.
 */

const CITY_SEO = {
  // ── España ────────────────────────────────────────────────────────────────
  'Madrid':        { seoCount: 800, minPrice: 500,  maxPrice: 800,  currency: 'EUR', label: 'la capital de España' },
  'Barcelona':     { seoCount: 150, minPrice: 450,  maxPrice: 750,  currency: 'EUR', label: 'la ciudad condal' },
  'Valencia':      { seoCount: 60,  minPrice: 350,  maxPrice: 550,  currency: 'EUR', label: 'la ciudad del Turia' },
  'Sevilla':       { seoCount: 25,  minPrice: 300,  maxPrice: 500,  currency: 'EUR', label: 'la perla de Andalucía' },
  'Málaga':        { seoCount: 40,  minPrice: 350,  maxPrice: 600,  currency: 'EUR', label: 'la Costa del Sol' },
  'Bilbao':        { seoCount: 15,  minPrice: 400,  maxPrice: 650,  currency: 'EUR', label: 'el País Vasco' },
  'Salamanca':     { seoCount: 20,  minPrice: 300,  maxPrice: 500,  currency: 'EUR', label: 'la ciudad universitaria' },
  'Alicante':      { seoCount: 15,  minPrice: 350,  maxPrice: 550,  currency: 'EUR', label: 'el Mediterráneo' },
  'Granada':       { seoCount: 20,  minPrice: 300,  maxPrice: 450,  currency: 'EUR', label: 'la Alhambra' },
  'Zaragoza':      { seoCount: 10,  minPrice: 250,  maxPrice: 450,  currency: 'EUR', label: 'el Ebro' },
  'Pamplona':      { seoCount: 8,   minPrice: 400,  maxPrice: 600,  currency: 'EUR', label: 'Navarra' },
  'Getafe':        { seoCount: 10,  minPrice: 400,  maxPrice: 650,  currency: 'EUR', label: 'sur de Madrid' },
  'Alcobendas':    { seoCount: 10,  minPrice: 500,  maxPrice: 750,  currency: 'EUR', label: 'norte de Madrid' },
  'San Sebastián': { seoCount: 10,  minPrice: 400,  maxPrice: 750,  currency: 'EUR', label: 'Donostia' },

  // ── US — Nueva York ───────────────────────────────────────────────────────
  'New York':      { seoCount: 444, minPrice: 1200, maxPrice: 4000, currency: 'USD', label: 'New York City' },
  'Manhattan':     { seoCount: 200, minPrice: 2000, maxPrice: 4500, currency: 'USD', label: 'Manhattan, NYC' },
  'Brooklyn':      { seoCount: 80,  minPrice: 1500, maxPrice: 3200, currency: 'USD', label: 'Brooklyn, NYC' },
  'Queens':        { seoCount: 40,  minPrice: 1200, maxPrice: 2600, currency: 'USD', label: 'Queens, NYC' },
  'Jersey City':   { seoCount: 10,  minPrice: 1500, maxPrice: 2800, currency: 'USD', label: 'Jersey City, NJ' },
  'Williamsburg':  { seoCount: 18,  minPrice: 1800, maxPrice: 3200, currency: 'USD', label: 'Williamsburg, Brooklyn' },
  'Astoria':       { seoCount: 12,  minPrice: 1400, maxPrice: 2400, currency: 'USD', label: 'Astoria, Queens' },

  // ── UK — Londres ──────────────────────────────────────────────────────────
  'London':        { seoCount: 535, minPrice: 800,  maxPrice: 2500, currency: 'GBP', label: 'London, UK' },
};

/** Symbols for currency display in SEO titles/descriptions */
const CURRENCY_SYMBOLS = { EUR: '€', USD: '$', GBP: '£' };

/**
 * Return the full SEO record for a city, or null if unknown.
 * @param {string} city
 * @returns {{ seoCount: number, minPrice: number, maxPrice: number, currency: string, label: string } | null}
 */
function getCitySeo(city) {
  return CITY_SEO[city] || null;
}

/**
 * Return the stable SEO listing count for a city.
 * Falls back to `fallback` (default 10) for unknown cities.
 */
function getCitySeoCount(city, fallback = 10) {
  return CITY_SEO[city]?.seoCount ?? fallback;
}

/**
 * Return { min, max } price object for a city.
 * Falls back to { min: 350, max: 800 } for unknown cities.
 */
function getCityPriceRange(city) {
  const d = CITY_SEO[city];
  if (!d) return { min: 350, max: 800 };
  return { min: d.minPrice, max: d.maxPrice };
}

/**
 * Return a formatted price-range string like "500-800" (no currency symbol).
 */
function getCityPriceRangeString(city) {
  const d = CITY_SEO[city];
  if (!d) return '350-800';
  return `${d.minPrice}-${d.maxPrice}`;
}

/**
 * Return a display-ready price range like "€500-€800" or "$1,200-$4,000".
 * Uses locale-aware formatting for USD/GBP (thousands separator).
 */
function getCityPriceDisplay(city) {
  const d = CITY_SEO[city];
  if (!d) return '€350-€800';
  const sym = CURRENCY_SYMBOLS[d.currency] || '€';
  if (d.currency === 'EUR') return `${sym}${d.minPrice}-${sym}${d.maxPrice}`;
  const fmt = (n) => n.toLocaleString('en-US');
  return `${sym}${fmt(d.minPrice)}-${sym}${fmt(d.maxPrice)}`;
}

module.exports = {
  CITY_SEO,
  CURRENCY_SYMBOLS,
  getCitySeo,
  getCitySeoCount,
  getCityPriceRange,
  getCityPriceRangeString,
  getCityPriceDisplay,
};
